import React, { useMemo, useCallback } from 'react';
import '../styles/DeliveryCost.css';
import { translations } from '../translations';

const DeliveryCost = ({ language }) => {
  const t = translations[language];
  
  const extractTimePeriod = useCallback((text) => {
    if (!text) return null;
    const timePattern = /([0-9೦-೯]+(?:\s*(?:[–-]|ರಿಂದ)\s*[0-9೦-೯]+)?\s*(?:months?|ತಿಂಗಳುಗಳ?ು|ವರ್ಷಗಳ?ು|ತಿಂಗಳಲ್ಲಿ|ತಿಂಗಳುಗಳಲ್ಲಿ))/gi;
    const match = text.match(timePattern);
    return match ? match[0] : null;
  }, []);

  const removeTimePeriod = useCallback((text) => {
    if (!text) return text;
    const timePattern = /([0-9೦-೯]+(?:\s*(?:[–-]|ರಿಂದ)\s*[0-9೦-೯]+)?\s*(?:months?|ತಿಂಗಳುಗಳ?ು|ವರ್ಷಗಳ?ು|ತಿಂಗಳಲ್ಲಿ|ತಿಂಗಳುಗಳಲ್ಲಿ))\s*(?:for|phased)?\s*[^\s]*/gi;
    let result = text.replace(timePattern, '').trim();
    result = result.replace(/^([0-9೦-೯]+(?:\s*(?:[–-]|ರಿಂದ)\s*[0-9೦-೯]+)?\s*(?:months?|ತಿಂಗಳುಗಳ?ು|ವರ್ಷಗಳ?ು|ತಿಂಗಳಲ್ಲಿ|ತಿಂಗಳುಗಳಲ್ಲಿ))\s*(?:for|phased)?\s*\n*\n*/gi, '');
    return result.trim();
  }, []);

  const highlightAmounts = useCallback((text) => {
    if (!text) return text;
    let result = text;
    const amountPattern = /(₹[0-9,೦-೯]+(?:\s*[–-]\s*[0-9,೦-೯]+)?\s*(?:crore|ಕೋಟಿ))/gi;
    result = result.replace(amountPattern, (match) => {
      const amountWithSuffix = language === 'kannada'
        ? match.replace(/\s*crore/gi, ' ಕೋಟಿ').replace(/\s*ಕೋಟಿ/gi, ' ಕೋಟಿ')
        : match.replace(/\s*(crore|ಕೋಟಿ)/gi, ' Cr');
      return `<strong class="amount-highlight">${amountWithSuffix}</strong>`;
    });
    const timePattern = /([0-9೦-೯]+(?:\s*(?:[–-]|ರಿಂದ)\s*[0-9೦-೯]+)?\s*(?:months?|ತಿಂಗಳುಗಳ?ು|ವರ್ಷಗಳ?ು|ತಿಂಗಳಲ್ಲಿ|ತಿಂಗಳುಗಳಲ್ಲಿ))/gi;
    result = result.replace(timePattern, (match) => `<strong class="amount-highlight">${match}</strong>`);
    return result;
  }, [language]);
  
  const costItems = useMemo(() => [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
          <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        </svg>
      ),
      title: typeof t.delivery.costItems.landAcquisition === 'object' ? t.delivery.costItems.landAcquisition.heading : t.delivery.costItems.landAcquisition,
      amount: typeof t.delivery.costItems.landAcquisition === 'object' ? (t.delivery.costItems.landAcquisition.content.match(/₹[0-9,೦-೯]+(?:\s*[–-]\s*[0-9,೦-೯]+)?\s*(?:crore|ಕೋಟಿ)/gi)?.[0] || t.delivery.costItems.landAcquisition.content.split(':')[1]?.trim()) : t.delivery.costItems.landAcquisitionAmount,
      description: typeof t.delivery.costItems.landAcquisition === 'object' ? t.delivery.costItems.landAcquisition.content : t.delivery.costItems.landAcquisitionDesc,
      readMoreContent: typeof t.delivery.costItems.landAcquisition === 'object' ? t.delivery.costItems.landAcquisition.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="6" width="20" height="8" rx="1"></rect>
          <path d="M17 14v7"></path>
          <path d="M7 14v7"></path>
          <path d="M17 3v3"></path>
          <path d="M7 3v3"></path>
          <path d="M10 14 2.3 6.3"></path>
          <path d="m14 6 7.7 7.7"></path>
          <path d="m8 6 8 8"></path>
        </svg>
      ),
      title: typeof t.delivery.costItems.civilWorks === 'object' ? t.delivery.costItems.civilWorks.heading : t.delivery.costItems.civilWorks,
      amount: typeof t.delivery.costItems.civilWorks === 'object' ? (t.delivery.costItems.civilWorks.content.match(/₹[0-9,೦-೯]+(?:\s*[–-]\s*[0-9,೦-೯]+)?\s*(?:crore|ಕೋಟಿ)/gi)?.[0] || t.delivery.costItems.civilWorks.content.split(':')[1]?.trim()) : t.delivery.costItems.civilWorksAmount,
      description: typeof t.delivery.costItems.civilWorks === 'object' ? t.delivery.costItems.civilWorks.content : t.delivery.costItems.civilWorksDesc,
      readMoreContent: typeof t.delivery.costItems.civilWorks === 'object' ? t.delivery.costItems.civilWorks.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
          <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        </svg>
      ),
      title: typeof t.delivery.costItems.rnr === 'object' ? t.delivery.costItems.rnr.heading : t.delivery.costItems.rnr,
      amount: typeof t.delivery.costItems.rnr === 'object' ? (t.delivery.costItems.rnr.content.match(/₹[0-9,೦-೯]+(?:\s*[–-]\s*[0-9,೦-೯]+)?\s*(?:crore|ಕೋಟಿ)/gi)?.[0] || t.delivery.costItems.rnr.content.split(':')[1]?.trim()) : t.delivery.costItems.rnrAmount,
      description: typeof t.delivery.costItems.rnr === 'object' ? t.delivery.costItems.rnr.content : t.delivery.costItems.rnrDesc,
      readMoreContent: typeof t.delivery.costItems.rnr === 'object' ? t.delivery.costItems.rnr.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"></path>
          <path d="M12 22v-3"></path>
        </svg>
      ),
      title: typeof t.delivery.costItems.environmental === 'object' ? t.delivery.costItems.environmental.heading : t.delivery.costItems.environmental,
      amount: typeof t.delivery.costItems.environmental === 'object' ? (t.delivery.costItems.environmental.content.match(/₹[0-9,೦-೯]+(?:\s*[–-]\s*[0-9,೦-೯]+)?\s*(?:crore|ಕೋಟಿ)/gi)?.[0] || t.delivery.costItems.environmental.content.split(':')[1]?.trim()) : t.delivery.costItems.environmentalAmount,
      description: typeof t.delivery.costItems.environmental === 'object' ? t.delivery.costItems.environmental.content : t.delivery.costItems.environmentalDesc,
      readMoreContent: typeof t.delivery.costItems.environmental === 'object' ? t.delivery.costItems.environmental.readMoreContent : null
    }
  ], [t.delivery.costItems]);

  return (
    <section id="delivery" className="delivery-section">
      <div className="container">
        <div className="delivery-content">
          <div className="section-header">
            <h2 className="section-title">{t.delivery.title}</h2>
            <h3 className="section-subtitle">{t.delivery.subtitle}</h3>
            <p className="section-description delivery-section-lead">
              {t.delivery.description} {t.delivery.descriptionReadMore && <span>{t.delivery.descriptionReadMore}</span>}
            </p>
          </div>
          {t.delivery.totalInvestment && typeof t.delivery.totalInvestment === 'object' && (
            <div className="investment-card">
              <div className="investment-label">{t.delivery.totalInvestment.heading}</div>
              <div 
                className="investment-amount"
                dangerouslySetInnerHTML={{ __html: highlightAmounts(t.delivery.totalInvestment.content) }}
              />
              {t.delivery.totalInvestment.readMoreContent && (
                <p className="investment-description">
                  {t.delivery.totalInvestment.contentFull && (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: highlightAmounts(t.delivery.totalInvestment.contentFull)
                      }}
                    />
                  )}
                  <span
                    className={t.delivery.totalInvestment.contentFull ? 'investment-description-more' : undefined}
                    dangerouslySetInnerHTML={{
                      __html: highlightAmounts(t.delivery.totalInvestment.readMoreContent)
                    }}
                  />
                </p>
              )}
            </div>
          )}
          <div className="cost-grid">
            {costItems.map((item, index) => (
              <div key={index} className="cost-card">
                <div className="cost-icon">{item.icon}</div>
                <div className="cost-details">
                  <h4 className="cost-title">{item.title}</h4>
                  {item.amount && (
                    <div 
                      className="cost-amount"
                      dangerouslySetInnerHTML={{ __html: highlightAmounts(item.amount) }}
                    />
                  )}
                  {(item.description || item.readMoreContent) && (
                    <p className="cost-description">
                      {item.description && (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: highlightAmounts(item.description)
                          }}
                        />
                      )}
                      {item.readMoreContent && (
                        <span
                          className={item.description ? 'cost-description-more' : undefined}
                          dangerouslySetInnerHTML={{
                            __html: highlightAmounts(item.readMoreContent)
                          }}
                        />
                      )}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="timeline-grid">
            {t.delivery.constructionTimeline && typeof t.delivery.constructionTimeline === 'object' && (
              <div className="timeline-card timeline-blue">
                <div className="timeline-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 6v6l4 2"></path>
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                </div>
                <div className="timeline-details">
                  <h4 className="timeline-title">{t.delivery.constructionTimeline.heading}</h4>
                  {extractTimePeriod(t.delivery.constructionTimeline.content) && (
                    <div 
                      className="timeline-amount"
                      dangerouslySetInnerHTML={{ 
                        __html: highlightAmounts(extractTimePeriod(t.delivery.constructionTimeline.content))
                      }}
                    />
                  )}
                  <p className="timeline-text">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: highlightAmounts(
                          t.delivery.constructionTimeline.contentFull ||
                            removeTimePeriod(t.delivery.constructionTimeline.content)
                        )
                      }}
                    />
                    {t.delivery.constructionTimeline.readMoreContent && (
                      <span
                        className="timeline-text-more"
                        dangerouslySetInnerHTML={{
                          __html: highlightAmounts(t.delivery.constructionTimeline.readMoreContent)
                        }}
                      />
                    )}
                  </p>
                </div>
              </div>
            )}
            {t.delivery.smartSystems && typeof t.delivery.smartSystems === 'object' && (
              <div className="timeline-card timeline-green">
                <div className="timeline-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="6" width="20" height="8" rx="1"></rect>
                    <path d="M17 14v7"></path>
                    <path d="M7 14v7"></path>
                    <path d="M17 3v3"></path>
                    <path d="M7 3v3"></path>
                    <path d="M10 14 2.3 6.3"></path>
                    <path d="m14 6 7.7 7.7"></path>
                    <path d="m8 6 8 8"></path>
                  </svg>
                </div>
                <div className="timeline-details">
                  <h4 className="timeline-title">{t.delivery.smartSystems.heading}</h4>
                  {extractTimePeriod(t.delivery.smartSystems.content) && (
                    <div 
                      className="timeline-amount"
                      dangerouslySetInnerHTML={{ 
                        __html: highlightAmounts(extractTimePeriod(t.delivery.smartSystems.content))
                      }}
                    />
                  )}
                  <p className="timeline-text">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: highlightAmounts(
                          t.delivery.smartSystems.contentFull ||
                            removeTimePeriod(t.delivery.smartSystems.content)
                        )
                      }}
                    />
                    {t.delivery.smartSystems.readMoreContent && (
                      <span
                        className="timeline-text-more"
                        dangerouslySetInnerHTML={{
                          __html: highlightAmounts(t.delivery.smartSystems.readMoreContent)
                        }}
                      />
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="delivery-message">
            <p 
              className="message-text"
              dangerouslySetInnerHTML={{ __html: t.delivery.message }}
            />
          </div>
          <div className="delivery-note">
            <p className="note-text">{t.delivery.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeliveryCost;
